import React from 'react'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  FormLabel,
  Menu,
  MenuItem,
  Modal,
  Switch,
  TextField,
} from '@mui/material'
import { EditInstagramToolObject } from 'graphql/generated/graphql'
import CloseIcon from '@mui/icons-material/Close'
import { MessageSquare, Activity, Edit2, Trash2, Check } from 'lucide-react'

interface ToolBoxProps {
  id: number
  title: string
  runs: number
  active: boolean
  statusMutation: (status: boolean, id: number) => Promise<void>
  deleteMutation: (id: number) => Promise<void>
  editMutation: (id: number, data: EditInstagramToolObject) => Promise<void>
  keyword: string
  shouldReply: boolean
  commentReply: Array<string | null>
  message: string[]
}

export default function ToolBox({
  title,
  runs,
  active,
  statusMutation,
  id,
  deleteMutation,
  keyword,
  shouldReply,
  commentReply,
  message,
  editMutation,
}: ToolBoxProps): JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openModal, setOpenModal] = React.useState(false)
  const [editData, setEditData] = React.useState<EditInstagramToolObject>({
    title,
    keyword,
    message,
    shouldReply,
    commentReply,
  })
  const open = Boolean(anchorEl)
  const newReplyRef = React.useRef<HTMLInputElement | null>(null)
  const newMessageRef = React.useRef<HTMLInputElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const glassmorphicInput = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(8px)',
      borderRadius: '10px',
      color: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(168, 85, 247, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(168, 85, 247, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#a855f7',
        borderWidth: '2px',
      },
      '&.Mui-disabled': {
        background: 'rgba(15, 23, 42, 0.3)',
        '& fieldset': {
          borderColor: 'rgba(168, 85, 247, 0.15)',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.6)',
      '&.Mui-focused': {
        color: '#a855f7',
      },
    },
  }

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '24px',
        background: 'rgba(30, 41, 59, 0.5)',
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        border: active
          ? '2px solid rgba(168, 85, 247, 0.5)'
          : '1px solid rgba(168, 85, 247, 0.2)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        '&:hover': {
          background: 'rgba(30, 41, 59, 0.7)',
          borderColor: 'rgba(168, 85, 247, 0.6)',
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(168, 85, 247, 0.2)',
        },
        '&::before': active
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #a855f7, #ec4899)',
            }
          : {},
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        <Box
          sx={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: active
              ? 'linear-gradient(135deg, #a855f7, #ec4899)'
              : 'linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(236, 72, 153, 0.3))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.3s ease',
          }}
        >
          <MessageSquare size={22} color="#ffffff" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: 700,
              color: '#ffffff',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h3>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                background: 'rgba(168, 85, 247, 0.15)',
                borderRadius: '8px',
                border: '1px solid rgba(168, 85, 247, 0.3)',
              }}
            >
              <Activity size={14} color="#a855f7" />
              <Box
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#a855f7',
                }}
              >
                {runs} runs
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: active ? '#10b981' : 'rgba(255, 255, 255, 0.3)',
                  boxShadow: active ? '0 0 8px #10b981' : 'none',
                }}
              />
              <Box
                sx={{
                  fontSize: '12px',
                  color: active ? '#10b981' : 'rgba(255, 255, 255, 0.5)',
                  fontWeight: 600,
                }}
              >
                {active ? 'Active' : 'Inactive'}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Switch
            checked={active}
            onChange={(e) => {
              void statusMutation(e.target.checked, id)
            }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#a855f7',
                '&:hover': {
                  backgroundColor: 'rgba(168, 85, 247, 0.08)',
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#a855f7',
              },
              '& .MuiSwitch-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          />
          <Button
            onClick={handleClick}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              padding: 0,
              color: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '10px',
              transition: 'all 0.2s',
              '&:hover': {
                background: 'rgba(168, 85, 247, 0.15)',
                color: '#ffffff',
              },
            }}
          >
            <MoreVertIcon />
          </Button>
        </Box>
      </Box>

      {/* Keyword Badge */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2))',
          borderRadius: '10px',
          border: '1px solid rgba(168, 85, 247, 0.3)',
        }}
      >
        <Box sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
          Keyword:
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {keyword}
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        id="tool-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'tool-menu-button',
        }}
        sx={{
          '& .MuiPaper-root': {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            color: '#ffffff',
            minWidth: '180px',
          },
          '& .MuiMenuItem-root': {
            padding: '12px 16px',
            gap: '12px',
            transition: 'all 0.2s',
            '&:hover': {
              background: 'rgba(168, 85, 247, 0.15)',
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenModal(true)
            handleClose()
          }}
        >
          <Edit2 size={16} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose()
            void deleteMutation(id)
          }}
          sx={{
            color: '#ef4444 !important',
          }}
        >
          <Trash2 size={16} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Modal */}
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false)
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 650,
            maxHeight: '85vh',
            overflowY: 'auto',
            bgcolor: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(20px)',
            color: '#ffffff',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            padding: '32px',
            borderRadius: '20px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(30, 41, 59, 0.3)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(168, 85, 247, 0.5)',
              borderRadius: '10px',
              '&:hover': {
                background: 'rgba(168, 85, 247, 0.7)',
              },
            },
          }}
        >
          {/* Modal Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Edit2 size={22} color="#ffffff" />
            </Box>
            <Box>
              <h2 style={{ margin: 0, fontSize: '26px', fontWeight: 700 }}>
                Edit Automation Tool
              </h2>
              <p style={{ margin: '4px 0 0 0', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Update your automation settings
              </p>
            </Box>
          </Box>

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Title Field */}
            <Box>
              <FormLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: 600, mb: 1, display: 'block' }}>
                Title
              </FormLabel>
              <TextField
                fullWidth
                defaultValue={title}
                placeholder="Enter automation title"
                onChange={(e) => {
                  setEditData((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }}
                sx={glassmorphicInput}
              />
            </Box>

            {/* Keyword Field */}
            <Box>
              <FormLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: 600, mb: 1, display: 'block' }}>
                Trigger Keyword
              </FormLabel>
              <TextField
                fullWidth
                defaultValue={keyword}
                placeholder="Enter keyword"
                onChange={(e) => {
                  setEditData((prev) => ({
                    ...prev,
                    keyword: e.target.value,
                  }))
                }}
                sx={glassmorphicInput}
              />
            </Box>

            {/* Should Reply Toggle */}
            <Box
              sx={{
                padding: '20px',
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <FormLabel sx={{ color: '#ffffff', fontSize: '15px', fontWeight: 600 }}>
                    Reply to Comments
                  </FormLabel>
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px', mt: 0.5 }}>
                    Enable automatic comment replies
                  </Box>
                </Box>
                <Switch
                  checked={editData.shouldReply}
                  onChange={(e) => {
                    setEditData((prev) => ({
                      ...prev,
                      shouldReply: e.target.checked,
                    }))
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#a855f7',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#a855f7',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Comment Replies */}
            {editData.shouldReply && (
              <Box>
                <FormLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: 600, mb: 2, display: 'block' }}>
                  Comment Replies
                </FormLabel>
                <Box sx={{ display: 'flex', gap: '12px', mb: 2 }}>
                  <TextField
                    fullWidth
                    inputRef={newReplyRef}
                    placeholder="Add a new reply..."
                    disabled={editData.commentReply.length === 3}
                    sx={glassmorphicInput}
                  />
                  <Button
                    variant="contained"
                    disabled={editData.commentReply.length === 3}
                    sx={{
                      minWidth: '120px',
                      background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #9333ea, #db2777)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(168, 85, 247, 0.2)',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                    onClick={() => {
                      if (newReplyRef?.current?.value) {
                        const text = newReplyRef.current?.value
                        newReplyRef.current.value = ''
                        setEditData((prev) => ({
                          ...prev,
                          commentReply: [...prev.commentReply, text],
                        }))
                      }
                    }}
                  >
                    Add Reply
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {editData.commentReply.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: 'rgba(15, 23, 42, 0.6)',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.2)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(15, 23, 42, 0.8)',
                          borderColor: 'rgba(168, 85, 247, 0.3)',
                        },
                      }}
                    >
                      <Box sx={{ color: '#ffffff', flex: 1, fontSize: '14px' }}>
                        {item}
                      </Box>
                      <CloseIcon
                        sx={{
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '20px',
                          transition: 'all 0.2s',
                          '&:hover': {
                            color: '#dc2626',
                            transform: 'scale(1.1)',
                          },
                        }}
                        onClick={() => {
                          setEditData((prev) => ({
                            ...prev,
                            commentReply: prev.commentReply.filter(
                              (x) => x !== item
                            ),
                          }))
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Direct Messages */}
            <Box>
              <FormLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: 600, mb: 2, display: 'block' }}>
                Direct Messages
              </FormLabel>
              <Box sx={{ display: 'flex', gap: '12px', mb: 2 }}>
                <TextField
                  fullWidth
                  inputRef={newMessageRef}
                  placeholder="Add a new message..."
                  disabled={editData.message.length === 3}
                  sx={glassmorphicInput}
                />
                <Button
                  variant="contained"
                  disabled={editData.message.length === 3}
                  sx={{
                    minWidth: '120px',
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9333ea, #db2777)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(168, 85, 247, 0.2)',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                  onClick={() => {
                    if (newMessageRef?.current?.value) {
                      const text = newMessageRef.current.value
                      newMessageRef.current.value = ''
                      setEditData((prev) => ({
                        ...prev,
                        message: [...prev.message, text],
                      }))
                    }
                  }}
                >
                  Add Message
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {editData.message.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: 'rgba(15, 23, 42, 0.6)',
                      borderRadius: '10px',
                      border: '1px solid rgba(168, 85, 247, 0.2)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'rgba(15, 23, 42, 0.8)',
                        borderColor: 'rgba(168, 85, 247, 0.3)',
                      },
                    }}
                  >
                    <Box sx={{ color: '#ffffff', flex: 1, fontSize: '14px' }}>
                      {item}
                    </Box>
                    <CloseIcon
                      sx={{
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '20px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          color: '#dc2626',
                          transform: 'scale(1.1)',
                        },
                      }}
                      onClick={() => {
                        setEditData((prev) => ({
                          ...prev,
                          message: prev.message.filter((x) => x !== item),
                        }))
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Modal Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid rgba(168, 85, 247, 0.2)',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpenModal(false)
              }}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'rgba(255, 255, 255, 0.8)',
                padding: '10px 24px',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={
                editData.shouldReply && editData.commentReply.length === 0
              }
              startIcon={<Check size={18} />}
              sx={{
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                padding: '10px 28px',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9333ea, #db2777)',
                  boxShadow: '0 6px 16px rgba(168, 85, 247, 0.4)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: 'rgba(255, 255, 255, 0.3)',
                  boxShadow: 'none',
                },
              }}
              onClick={() => {
                editMutation(id, {
                  ...editData,
                  commentReply: editData.shouldReply
                    ? editData.commentReply
                    : [],
                })
                  .then(() => {
                    setOpenModal(false)
                  })
                  .catch((e) => {
                    console.error(e)
                  })
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}